import React, { useState, useEffect } from 'react';
import patientService from '../../services/patientService';

const PatientDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [nearbyHospitals, setNearbyHospitals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    loadDashboard();
    getUserLocation();
  }, []);

  useEffect(() => {
    if (userLocation) {
      loadNearbyHospitals();
    }
  }, [userLocation]);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setUserLocation({ latitude: -1.2921, longitude: 36.8219 });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      () => {
        setUserLocation({ latitude: -1.2921, longitude: 36.8219 });
      }
    );
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const dashResponse = await patientService.getDashboardData();
      setDashboardData(dashResponse.data);

      const notifResponse = await patientService.getNotifications({ limit: 5 });
      setNotifications(notifResponse.data?.notifications || []);

    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadNearbyHospitals = async () => {
    if (!userLocation) return;

    try {
      const response = await patientService.getNearbyHospitals({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        limit: 3,
        radius: 10000
      });

      setNearbyHospitals(response.data || []);
    } catch (err) {
      try {
        const allHospitals = await patientService.getHospitals({ limit: 3 });
        setNearbyHospitals(allHospitals.data?.hospitals || []);
      } catch (fallbackErr) {
        setNearbyHospitals([]);
      }
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await patientService.markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, isRead: true } : n
        )
      );
    } catch (err) {
      return;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Dashboard</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadDashboard}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold mb-6">Patient Dashboard</h1>

      {dashboardData?.profile && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center">
            {dashboardData.profile.avatar && (
              <img
                src={dashboardData.profile.avatar}
                alt="Profile"
                className="w-16 h-16 rounded-full mr-4"
              />
            )}
            <div>
              <h2 className="text-xl font-semibold">{dashboardData.profile.fullName}</h2>
              <p className="text-gray-600">
                Age: {dashboardData.profile.age} | Blood Group: {dashboardData.profile.bloodGroup}
              </p>
            </div>
          </div>
        </div>
      )}

      {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
        <div className="mb-6">
          {dashboardData.alerts.map((alert, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg mb-2 ${
                alert.type === 'danger'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : alert.type === 'warning'
                  ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  : 'bg-blue-50 text-blue-800 border border-blue-200'
              }`}
            >
              <p className="font-medium">{alert.message}</p>
              <button className="text-sm underline mt-1">{alert.action}</button>
            </div>
          ))}
        </div>
      )}

      {dashboardData?.stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Consultations"
            value={dashboardData.stats.totalConsultations}
            icon="📋"
          />
          <StatCard
            title="Active Medications"
            value={dashboardData.stats.activeMedications}
            icon="💊"
          />
          <StatCard
            title="Allergies"
            value={dashboardData.stats.allergiesCount}
            icon="⚠️"
          />
          <StatCard
            title="Chronic Conditions"
            value={dashboardData.stats.chronicConditionsCount}
            icon="🏥"
          />
        </div>
      )}

      {nearbyHospitals.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-semibold mb-4">Nearby Hospitals</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {nearbyHospitals.map((hospital) => (
              <HospitalCard key={hospital._id} hospital={hospital} />
            ))}
          </div>
        </div>
      )}

      {notifications.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Recent Notifications</h3>
          <div className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification._id}
                className={`p-3 rounded cursor-pointer ${
                  notification.isRead ? 'bg-gray-50' : 'bg-blue-50'
                }`}
                onClick={() => markNotificationAsRead(notification._id)}
              >
                <p className="font-medium">{notification.title}</p>
                <p className="text-sm text-gray-600">{notification.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>
      <div className="text-3xl">{icon}</div>
    </div>
  </div>
);

const HospitalCard = ({ hospital }) => (
  <div className="border rounded-lg p-4 hover:shadow-lg transition">
    <h4 className="font-semibold mb-2">{hospital.name}</h4>
    <p className="text-sm text-gray-600 mb-2">{hospital.location?.address}</p>
    {hospital.rating && (
      <div className="flex items-center text-sm">
        <span className="text-yellow-500">⭐</span>
        <span className="ml-1">{hospital.rating.average}</span>
        <span className="ml-1 text-gray-500">
          ({hospital.rating.totalReviews} reviews)
        </span>
      </div>
    )}
  </div>
);

export default PatientDashboard;